import React from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

/**
 * SuccessSkeleton - Layout tipo Company Data
 * Card centrado con avatar, nombre, divider, 3 líneas gruesas de información
 */
const SuccessSkeleton: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const skeletonColors = {
    page: theme.palette.mode === 'light' ? '#f6f8fb' : '#10141C',
    block: theme.palette.mode === 'light' ? '#e9edf2' : '#2f363e',
    paper: theme.palette.mode === 'light' ? '#ffffff' : '#1a1f2a',
    shine: theme.palette.mode === 'light' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.08)'
  };

  const skeletonStyles = `
    .success-skeleton-page {
      min-height: 100vh;
      background: ${skeletonColors.page};
      display: flex;
      flex-direction: column;
    }

    .success-skeleton-header {
      background: ${skeletonColors.paper};
      border-bottom: 1px solid ${theme.palette.mode === 'light' ? '#e0e0e0' : '#2f363e'};
    }

    .success-skeleton-header-inner {
      max-width: 1152px;
      margin: 0 auto;
      padding: 16px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .success-skel-logo-container {
      min-width: ${isMobile ? 'auto' : '200px'};
      display: flex;
      align-items: center;
    }

    .success-skel-logo {
      width: 120px;
      height: 40px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    .success-skel-close-container {
      min-width: ${isMobile ? 'auto' : '200px'};
      display: flex;
      justify-content: flex-end;
    }

    .success-skel-close {
      width: ${isMobile ? '40px' : '36px'};
      height: ${isMobile ? '40px' : '36px'};
      background: ${skeletonColors.block};
      border-radius: 50%;
    }

    .success-skeleton-content {
      flex: 1;
      display: flex;
      justify-content: center;
      padding: ${isMobile ? '24px 16px' : '75px 24px 24px'};
    }

    .success-skeleton-card {
      background: ${skeletonColors.paper};
      border-radius: 16px;
      width: 100%;
      max-width: 600px;
      padding: 40px 32px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .success-skel-avatar {
      width: ${isMobile ? '80px' : '100px'};
      height: ${isMobile ? '80px' : '100px'};
      background: ${skeletonColors.block};
      border-radius: 50%;
      margin-bottom: 20px;
    }

    .success-skel-name {
      width: 180px;
      height: 24px;
      background: ${skeletonColors.block};
      border-radius: 6px;
      margin-bottom: 12px;
    }

    .success-skel-email {
      width: 220px;
      height: 16px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    .success-skel-divider {
      width: 100%;
      height: 1px;
      background: ${theme.palette.mode === 'light' ? '#e8e8e8' : '#2f363e'};
      margin: 28px 0;
    }

    .success-skel-details {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 20px;
    }

    .success-skel-detail-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    /* Líneas más gruesas */
    .success-skel-label {
      width: 40%;
      height: 20px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    /* Valores pequeños (montos) */
    .success-skel-value {
      width: 25%;
      height: 20px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    /* Footer */
    .success-skeleton-footer {
      padding: 24px;
      display: flex;
      justify-content: center;
    }

    .success-skel-footer-logo {
      width: 100px;
      height: 24px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    /* Shimmer animation */
    .shimmer {
      position: relative;
      overflow: hidden;
    }

    .shimmer::after {
      content: "";
      position: absolute;
      inset: 0;
      transform: translateX(-150%);
      background: linear-gradient(90deg, transparent 0%, ${skeletonColors.shine} 50%, transparent 100%);
      animation: successShimmer 1.6s ease-in-out infinite;
    }

    @keyframes successShimmer {
      0% { transform: translateX(-150%); }
      100% { transform: translateX(150%); }
    }
  `;

  return (
    <div className="success-skeleton-page">
      <style>{skeletonStyles}</style>
      
      {/* Header */}
      <div className="success-skeleton-header">
        <div className="success-skeleton-header-inner">
          <div className="success-skel-logo-container">
            <div className="success-skel-logo shimmer" />
          </div>
          <div className="success-skel-close-container">
            <div className="success-skel-close shimmer" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="success-skeleton-content">
        <div className="success-skeleton-card">
          <div className="success-skel-avatar shimmer" />
          <div className="success-skel-name shimmer" />
          <div className="success-skel-email shimmer" />
          
          <div className="success-skel-divider" />
          
          {/* 3 líneas gruesas con valores pequeños */}
          <div className="success-skel-details">
            <div className="success-skel-detail-row">
              <div className="success-skel-label shimmer" />
              <div className="success-skel-value shimmer" />
            </div>
            <div className="success-skel-detail-row">
              <div className="success-skel-label shimmer" />
              <div className="success-skel-value shimmer" />
            </div>
            <div className="success-skel-detail-row">
              <div className="success-skel-label shimmer" />
              <div className="success-skel-value shimmer" />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="success-skeleton-footer">
        <div className="success-skel-footer-logo shimmer" />
      </div>
    </div>
  );
};

export default SuccessSkeleton;
